import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { useTranslation } from '@repo/i18n'
import { Menu, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import {
	createContext,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useContext,
	useState
} from 'react'
import { useHoverEffect } from '#aceternity/card-hover-effect'
import { cn } from '#lib/utils'

/*
 * Aceternity UI sidebar (ui.aceternity.com/components/sidebar), adapted:
 * - the desktop panel expands OVER the page instead of pushing it, so hovering
 *   the rail never re-lays out the data grid next to it;
 * - it also opens on keyboard focus, not only on hover;
 * - theme tokens instead of fixed neutrals, lucide instead of tabler icons;
 * - links render through `render` (e.g. a router <Link>), not a bare <a href>;
 * - inside a HoverEffectGroup the hover background slides between links.
 */

const RAIL_WIDTH = 56
const OPEN_WIDTH = 232

const SidebarContext = createContext<
	| {
			open: boolean
			setOpen: Dispatch<SetStateAction<boolean>>
			animate: boolean
	  }
	| undefined
>(undefined)

export function useSidebar() {
	const context = useContext(SidebarContext)
	if (!context) {
		throw new Error('useSidebar must be used within a Sidebar')
	}
	return context
}

export function Sidebar(props: {
	children: ReactNode
	open?: boolean
	setOpen?: Dispatch<SetStateAction<boolean>>
	animate?: boolean
}) {
	const [openState, setOpenState] = useState(false)

	return (
		<SidebarContext.Provider
			value={{
				open: props.open ?? openState,
				setOpen: props.setOpen ?? setOpenState,
				animate: props.animate ?? true
			}}
		>
			{props.children}
		</SidebarContext.Provider>
	)
}

export function SidebarBody(props: {
	className?: string
	children: ReactNode
}) {
	return (
		<>
			<DesktopSidebar className={props.className}>
				{props.children}
			</DesktopSidebar>
			<MobileSidebar className={props.className}>
				{props.children}
			</MobileSidebar>
		</>
	)
}

function DesktopSidebar(props: { className?: string; children: ReactNode }) {
	const { open, setOpen, animate } = useSidebar()
	const reduceMotion = useReducedMotion()
	const expanded = !animate || open

	return (
		// The slot keeps the rail's width in the layout; the panel inside grows
		// past it and floats above the page.
		<div
			className='relative hidden h-full shrink-0 md:block'
			style={{ width: animate ? RAIL_WIDTH : OPEN_WIDTH }}
		>
			<motion.div
				animate={{ width: expanded ? OPEN_WIDTH : RAIL_WIDTH }}
				className={cn(
					'group/sidebar absolute inset-y-0 left-0 z-40 flex h-full flex-col overflow-hidden bg-sidebar px-2 py-3 text-sidebar-foreground',
					open &&
						animate &&
						'shadow-[8px_0_32px_-16px_oklch(0.3_0.02_55/0.35)]',
					props.className
				)}
				data-state={expanded ? 'open' : 'closed'}
				initial={false}
				onBlur={(event) => {
					if (!event.currentTarget.contains(event.relatedTarget)) {
						setOpen(false)
					}
				}}
				onFocus={(event) => {
					// Keyboard focus only: a menu handing focus back to its trigger
					// after a click would reopen the rail under the pointer.
					if (event.target.matches(':focus-visible')) {
						setOpen(true)
					}
				}}
				onMouseEnter={() => setOpen(true)}
				onMouseLeave={() => setOpen(false)}
				transition={
					reduceMotion
						? { duration: 0 }
						: { type: 'spring', stiffness: 380, damping: 36 }
				}
			>
				{props.children}
			</motion.div>
		</div>
	)
}

function MobileSidebar(props: { className?: string; children: ReactNode }) {
	const { open, setOpen } = useSidebar()
	const { t } = useTranslation()

	return (
		<div className='flex h-12 w-full shrink-0 items-center justify-end bg-sidebar px-3 md:hidden'>
			<button
				aria-label={t('shared:sidebar.open')}
				className='flex size-9 items-center justify-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent'
				onClick={() => setOpen(true)}
				type='button'
			>
				<Menu className='size-5' />
			</button>
			<AnimatePresence>
				{open && (
					<motion.div
						animate={{ x: 0, opacity: 1 }}
						className={cn(
							'group/sidebar fixed inset-0 z-50 flex h-full w-full flex-col bg-sidebar p-6 text-sidebar-foreground',
							props.className
						)}
						data-state='open'
						exit={{ x: '-100%', opacity: 0 }}
						initial={{ x: '-100%', opacity: 0 }}
						transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
					>
						<button
							aria-label={t('shared:sidebar.close')}
							className='absolute top-5 right-5 z-50 flex size-9 items-center justify-center rounded-md hover:bg-sidebar-accent'
							onClick={() => setOpen(false)}
							type='button'
						>
							<X className='size-5' />
						</button>
						{props.children}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}

export function SidebarLink(
	props: useRender.ComponentProps<'a'> & {
		icon: ReactNode
		label: string
	}
) {
	const { open, animate } = useSidebar()
	const { icon, label, render, className, ...rest } = props
	// The current page already sits on its own sheet: no highlight over it.
	const hover = useHoverEffect(
		label,
		'bg-sidebar-accent group-data-[status=active]/sidebar-link:hidden'
	)

	return useRender({
		defaultTagName: 'a',
		render,
		props: mergeProps<'a'>(
			{
				className: cn(
					'group/sidebar-link relative isolate flex h-9 shrink-0 items-center gap-3 rounded-md px-2.5 text-sm transition-colors hover:text-sidebar-accent-foreground focus-visible:outline-2 focus-visible:outline-sidebar-ring [&_svg]:size-[18px] [&_svg]:shrink-0',
					!hover && 'hover:bg-sidebar-accent',
					className
				),
				onMouseEnter: hover?.onMouseEnter,
				children: (
					<>
						{hover?.highlight}
						{icon}
						<motion.span
							animate={{ opacity: !animate || open ? 1 : 0 }}
							className='whitespace-pre transition-transform duration-150 group-hover/sidebar-link:translate-x-0.5'
							initial={false}
						>
							{label}
						</motion.span>
					</>
				)
			},
			rest
		)
	})
}
